import { useState, useEffect } from 'react';
import axiosDotnet from '../services/axiosDotnet';
import { showError, showSuccess, showWarning } from '../services/reactToastify';
import axiosExpress from '../services/axiosExpress';
import { forgeAssistant } from '../services/groqService';

export default function CreateTaskModal({ projects, workspace, projectId, isOpen, onClose, onTaskCreated }) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [assignedTo, setAssignedTo] = useState('');
    const [assignedUserName, setAssignedUserName] = useState("");
    const [loading, setLoading] = useState(false);
    const [projectMembers, setProjectMembers] = useState([]);
    const currentProject = projects.filter( p => p?.id == projectId)[0]
    const [tasks, setTasks] = useState([]);

    // console.log("Current Project => ", currentProject)

    useEffect(() => {
        if (isOpen && projectId) {
            fetchProjectMembers();
            fetchTasks();
        }
    }, [isOpen, projectId]);

    const fetchProjectMembers = async () => {
        try {
            const res = await axiosDotnet.get(`/api/projects/${projectId}/members`);
            // console.log("Project Members Loaded:", res.data);
            setProjectMembers(res.data);
        } catch (err) {
            console.error(err);
            showError("Failed to load project members");
        }
    };

    const fetchTasks = async () => {
        try {
            // console.log(projectId)
            const res = await axiosExpress.get(`/api/kanban/project/${projectId}`);
            // console.log(res?.data)
            setTasks(res?.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // console.log(assignedTo, assignedUserName)
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim()) return showWarning("Task title is required");

        setLoading(true);
        try {
            const response = await axiosDotnet.post('/api/tasks', {
                projectId: projectId,
                title: title.trim(),
                description: description.trim() || null,
                dueDate: dueDate || null,
                assignedTo: assignedTo || null,
                assignedUserName: assignedUserName.toString() || null,
                status: "TODO"
            });

            showSuccess("Task created successfully!");
            onTaskCreated?.(response.data);
            onClose();

            // Reset form
            setTitle('');
            setDescription('');
            setDueDate('');
            setAssignedTo('');
            setAssignedUserName("")
        } catch (err) {
            console.error(err);
            showSuccess(err?.response?.data?.message || "Failed to create task");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // ================= AI Integrate ===============>

    const [prompt, setPrompt] = useState('');
    const [response, setResponse] = useState('');
    
      useEffect(() => {

    const projectPromptTemplate = `
    You are an expert Project Manager, Streamlined Flow Task Creator and Technical Architect.

    **Workspace Name:** ${workspace?.name}
    **Workspace Description:** ${workspace?.description}
    **Project Name:** ${currentProject?.title}
    **Project Description:** ${currentProject?.description}

    // Change the tasks mapping section to this:
    **Tasks inside this given current Project:** ${
        Array.isArray(tasks) 
            ? tasks.map((t, idx) => `
              task${idx}: ${t?.Title || t?.title || 'Untitled'}
              task${idx} Description: ${t?.Description || t?.description || 'No description'}
              task${idx} Status: ${t?.Status || t?.status || 'Unknown'}
            `).join('') // <--- THE KEY FIX: Join the array into a string
            : "No tasks found."
    }

    Task:
    Create **ONE unique, specific, and actionable task to do next ** that is **inside** this project which is inside this workspace.

    Important Rules:
    - Do NOT repeat or copy ideas from previous responses.
    - The task must be **related** to the project but **NOT a copy** of it.
    // - Do NOT use words like "Platform", "Hub", "System", or "Community" in the title if they are already in the workspace name.
    - Make the task title and description **more specific and focused**.
    - Be creative and specific.
    - The task should be different every time this prompt is called.
    - Think of a **concrete initiative** or deliverable within this workspace.

    Respond **only** with valid JSON in this exact format. No extra text, no explanations, no markdown.

    {
      "Title": "Unique and specific next to do task title",
      "Description": "Detailed, focused task description (Line Clamped to Max 1-2 Lines Strinctly not more than that, very shortes description as possible)",
      "Tools": ["Tool1", "Tool2", "Tool3"],
      "Technologies": ["Tech1", "Tech2", "Tech3"],
    }
    `;

      const randomSeed = Math.random().toString(36).substring(2, 10);

      const finalPrompt = projectPromptTemplate + `\n\nGenerate a completely new idea. Random seed: ${randomSeed}`

      setPrompt(finalPrompt);

      }, [workspace, tasks])

    //   console.log(tasks)
    //   console.log(prompt)

      const handleAsk = async () => {
        if (!prompt.trim()) return;

        setLoading(true);

        try {
          const result = await forgeAssistant(prompt);

        //   console.log("Raw response from Groq:", result);
          let parsedData;

          if (typeof result === 'object' && result !== null) {
            parsedData = result;
          }
          else if (typeof result === 'string') {
            let cleanResult = result
              .replace(/```json\n?/g, '')
              .replace(/```\n?/g, '')
              .trim();

            parsedData = JSON.parse(cleanResult);
          }

          setResponse(parsedData);
        //   console.log("Successfully parsed JSON:", parsedData);
          setTitle(parsedData?.Title)
          setDescription(parsedData?.Description)
        } catch (err) {
          setResponse("Sorry, AI is currently unavailable.");
        } finally {
          setLoading(false);
        }
      }



    // ================= AI Integrate ===============>

    return (
        <div className="fixed my-5 py-5 inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="my-5 py-5 bg-zinc-900 border border-zinc-700 rounded-3xl w-full max-w-lg p-8">
                <h2 className="text-2xl font-semibold mb-6">Create New Task</h2>

                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label className="block text-sm text-zinc-400 mb-2">Task Title *</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-5 py-3 text-white"
                            placeholder="Implement user authentication"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-zinc-400 mb-2">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-5 py-3 text-white"
                            placeholder="Add detailed description..."
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-zinc-400 mb-2">Due Date</label>
                            <input
                                type="date"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-5 py-3 text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-zinc-400 mb-2">Assign To</label>
                            <select
                                value={assignedTo}

                                onChange={(e) => {
                                    const selectedUserId = e.target.value;
                                    setAssignedTo(selectedUserId);

                                    const selectedMember = projectMembers.find(m => m.userId === selectedUserId);
                                    setAssignedUserName(selectedMember?.name || '');
                                }}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl px-5 py-3 text-white"
                            >
                                {projectMembers?.map((m) => (
                                    <option key={m.userId} value={m.userId}>
                                        {m.name} ({m.roleInProject})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 cursor-pointer py-3 border border-zinc-600 rounded-2xl hover:bg-zinc-800"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="cursor-pointer flex-1 py-3 bg-violet-600 hover:bg-violet-500 rounded-2xl font-semibold disabled:opacity-50"
                        >
                            {loading ? "Creating..." : "Create Task"}
                        </button>
                    </div>

                </form>
                <button
                    onClick={handleAsk}
                    // disabled={loading}
                    className="w-full mt-5 cursor-pointer bg-violet-600 hover:bg-violet-500 disabled:bg-violet-800 py-3 rounded-2xl font-semibold text-lg transition-all"
                >
                    AI Task Suggestions
                </button>
            </div>
        </div>
    );
}